import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, Paper, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { ArrowBack, AssignmentTurnedIn, EventRepeat, ChevronRight, Add, Edit, Delete, Save, Description } from '@mui/icons-material';

const DEFAULT_PROTOCOLS = [
  {
    id: 'fechamento_area',
    title: 'Protocolo: Fechamento de Área',
    desc: 'Passo a passo para consolidação e encerramento de uma localidade.',
    color: '#00b8cc',
    content: `1. Garantir que 100% dos quarteirões foram trabalhados e concluídos no TOVIS.
2. Realizar a conferência de imóveis individualizada (Fase 2).
3. Verificar a Tabela de Pendências para a área e garantir que não há recuperações em aberto não lançadas.
4. Conferir as amostras de laboratório vinculadas a esta área (se todas estão com resultado lançado).
5. Imprimir/gerar o FAD-07 consolidado da área.
6. Imprimir/gerar o FA-D-06 (Reconhecimento Geográfico) atualizado.`
  },
  {
    id: 'fechamento_ciclo',
    title: 'Protocolo: Fechamento de Ciclo',
    desc: 'Rotina de encerramento bimestral para envio de indicadores.',
    color: '#e0242b',
    content: `1. Assegurar que todas as Áreas do município foram fechadas (ver Protocolo de Fechamento de Área).
2. Consolidar o Resumo Geral do município.
3. Emitir relatório final de Pendências não recuperadas no ciclo para planejamento do próximo.
4. Atualizar o sistema TOVIS (Cadastro de Áreas) para o novo Ciclo.
5. Zerar contadores de pendência física (arquivamento de FAD-01 antigos).`
  }
];

const COLORS_PALETTE = ['#00b8cc', '#34d399', '#fbbf24', '#a78bfa', '#2dd4bf', '#e0242b'];

export default function Protocolos({ onVoltar }: { onVoltar: () => void }) {
  const [protocolos, setProtocolos] = useState(() => {
    const saved = localStorage.getItem('tovis_protocolos_defs');
    return saved ? JSON.parse(saved) : DEFAULT_PROTOCOLS;
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  
  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ id: '', title: '', desc: '', content: '', color: '#00b8cc' });

  const saveProtocolos = (newData: any) => {
    setProtocolos(newData);
    localStorage.setItem('tovis_protocolos_defs', JSON.stringify(newData));
  };

  const handleOpenNew = () => {
    setForm({ id: `prot_${Date.now()}`, title: '', desc: '', content: '', color: COLORS_PALETTE[Math.floor(Math.random() * COLORS_PALETTE.length)] });
    setModalOpen(true);
  };

  const handleOpenEdit = (prot: any) => {
    setForm({ ...prot });
    setModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Tem certeza que deseja excluir este protocolo?")) {
      const newData = protocolos.filter((p: any) => p.id !== id);
      saveProtocolos(newData);
      if (selectedId === id) setSelectedId(null);
    }
  };

  const handleSaveModal = () => {
    const existsIndex = protocolos.findIndex((p: any) => p.id === form.id);
    const newData = [...protocolos];
    if (existsIndex >= 0) {
      newData[existsIndex] = form;
    } else {
      newData.push(form);
    }
    saveProtocolos(newData);
    setModalOpen(false);
  };

  const active = protocolos.find((p: any) => p.id === selectedId);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#030b16', color: '#e0e8f0' }}>
      <Box sx={{ bgcolor: '#081629', borderBottom: `2px solid ${active ? active.color + '40' : 'rgba(255,255,255,0.08)'}`, px: { xs: 2, md: 4 }, py: 2, transition: 'border-color 0.4s' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 1100, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => selectedId ? setSelectedId(null) : onVoltar()} 
              sx={{ color: active ? active.color : '#00b8cc' }}
            >
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#f0f4f8' }}>
                {selectedId ? active?.title : 'Protocolos de Sistema'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#8b9eb0' }}>
                {selectedId ? 'Diretrizes oficiais e operacionais' : 'Documentação padrão e fluxos de trabalho'}
              </Typography>
            </Box>
          </Box>
          {!selectedId && (
            <Button 
              variant={editMode ? "contained" : "outlined"}
              onClick={() => setEditMode(!editMode)}
              startIcon={editMode ? <Save /> : <Edit />}
              sx={{ 
                color: editMode ? '#fff' : '#00b8cc', 
                bgcolor: editMode ? '#00b8cc' : 'transparent',
                borderColor: editMode ? '#00b8cc' : 'rgba(0,184,204,0.5)', 
                textTransform: 'none', px: 2,
                '&:hover': { bgcolor: editMode ? '#0095a8' : 'rgba(0,184,204,0.1)', borderColor: '#00b8cc' }
              }}
            >
              {editMode ? 'Concluir Edição' : 'Editar Lista'}
            </Button>
          )}
          {selectedId && (
            <Button 
              variant="outlined"
              onClick={() => handleOpenEdit(active)}
              startIcon={<Edit />}
              sx={{ color: active.color, borderColor: `${active.color}50`, textTransform: 'none', '&:hover': { bgcolor: `${active.color}15`, borderColor: active.color } }}
            >
              Editar Protocolo
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, px: { xs: 2, md: 4 }, pb: 8 }}>
        
        {!selectedId ? (
          <>
            {editMode && (
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" startIcon={<Add />} onClick={handleOpenNew} sx={{ bgcolor: '#00b8cc', color: '#fff', '&:hover': { bgcolor: '#0095a8' } }}>
                  Novo Protocolo
                </Button>
              </Box>
            )}
            <Paper elevation={0} sx={{ bgcolor: '#0c1c30', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 }}>
              <List disablePadding>
                {protocolos.map((p: any, idx: number) => (
                  <React.Fragment key={p.id}>
                    <ListItem 
                      disablePadding
                      secondaryAction={
                        editMode && (
                          <Box>
                             <IconButton edge="end" onClick={() => handleOpenEdit(p)} sx={{ color: '#8b9eb0', mr: 1 }}>
                               <Edit fontSize="small" />
                             </IconButton>
                             <IconButton edge="end" onClick={() => handleDelete(p.id)} sx={{ color: '#e0242b' }}>
                               <Delete fontSize="small" />
                             </IconButton>
                          </Box>
                        )
                      }
                    >
                      <ListItemButton 
                        onClick={() => !editMode && setSelectedId(p.id)}
                        disableRipple={editMode}
                        sx={{ py: 2, px: 3, cursor: editMode ? 'default' : 'pointer', '&:hover': { bgcolor: editMode ? 'transparent' : `${p.color}08` } }}
                      >
                        <ListItemIcon sx={{ minWidth: 48 }}>
                          <Box sx={{ bgcolor: `${p.color}15`, color: p.color, p: 1, borderRadius: 1.5, display: 'flex' }}>
                            <Description />
                          </Box>
                        </ListItemIcon>
                        <ListItemText 
                          primary={<Typography sx={{ fontWeight: 600, color: '#e7eef7' }}>{p.title}</Typography>}
                          secondary={<Typography sx={{ fontSize: '13px', color: '#8b9eb0', mt: 0.5 }}>{p.desc}</Typography>}
                        />
                        {!editMode && <ChevronRight sx={{ color: p.color }} />}
                      </ListItemButton>
                    </ListItem>
                    {idx < protocolos.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />}
                  </React.Fragment>
                ))}
                {protocolos.length === 0 && (
                  <Box sx={{ p: 4, textAlign: 'center', color: '#8b9eb0' }}>
                    Nenhum protocolo cadastrado.
                  </Box>
                )}
              </List>
            </Paper>
          </>
        ) : (
          <Paper elevation={0} sx={{ bgcolor: '#0c1c30', border: `1px solid ${active.color}30`, borderRadius: 2, p: { xs: 3, md: 4 } }}>
            <Box sx={{ color: '#e7eef7', fontSize: '16px', lineHeight: 1.8, whiteSpace: 'pre-wrap', fontFamily: "'Inter', sans-serif" }}>
              {active?.content}
            </Box>
          </Paper>
        )}

      </Box>

      {/* MODAL DE PROTOCOLO */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth sx={{ '& .MuiDialog-paper': { bgcolor: '#0c1c30', color: '#f0f4f8' } }}>
        <DialogTitle>{form.id.startsWith('prot_') ? 'Novo Protocolo' : 'Editar Protocolo'}</DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <TextField
            fullWidth label="Título" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
            variant="outlined" sx={{ mb: 3, mt: 1, '& .MuiInputBase-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: '#8b9eb0' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
          />
          <TextField
            fullWidth label="Descrição Curta" value={form.desc} onChange={(e) => setForm({...form, desc: e.target.value})}
            variant="outlined" sx={{ mb: 3, '& .MuiInputBase-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: '#8b9eb0' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
          />
          <TextField
            fullWidth label="Conteúdo / Passo a passo" multiline rows={6} value={form.content} onChange={(e) => setForm({...form, content: e.target.value})}
            variant="outlined" sx={{ mb: 3, '& .MuiInputBase-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: '#8b9eb0' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
          />
          <Typography variant="body2" sx={{ color: '#8b9eb0', mb: 1 }}>Cor do Card:</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {COLORS_PALETTE.map(c => (
              <Box 
                key={c} onClick={() => setForm({...form, color: c})}
                sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: c, cursor: 'pointer', border: form.color === c ? '3px solid #fff' : '2px solid transparent' }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)} sx={{ color: '#8b9eb0' }}>Cancelar</Button>
          <Button onClick={handleSaveModal} variant="contained" sx={{ bgcolor: form.color, color: '#fff', '&:hover': { filter: 'brightness(0.9)' } }}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
